import React, { type ReactNode } from "react";
import { AlertTriangle, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

/** Skeleton placeholder while VB data loads. Reused across every VB screen. */
export function VbLoading({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-3", className)} aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-16 animate-pulse rounded-vb-lg border border-vb-line bg-vb-bg-subtle"
        />
      ))}
    </div>
  );
}

/** Empty state - neutral card with an icon, message and optional action. */
export function VbEmpty({
  title,
  description,
  icon,
  action,
  className,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-vb-lg border border-dashed border-vb-line bg-vb-surface px-6 py-12 text-center",
        className,
      )}
    >
      <div className="grid h-10 w-10 place-items-center rounded-vb-pill bg-vb-bg-subtle text-vb-text-3">
        {icon ?? <Inbox className="h-5 w-5" />}
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-vb-text">{title}</p>
        {description && <p className="max-w-sm text-sm text-vb-text-2">{description}</p>}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/** Error state - message plus an optional retry affordance. */
export function VbError({
  title = "Something went wrong",
  description,
  onRetry,
  className,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-vb-lg border border-vb-fail-line bg-vb-fail-bg px-6 py-12 text-center",
        className,
      )}
      role="alert"
    >
      <div className="grid h-10 w-10 place-items-center rounded-vb-pill bg-vb-surface text-vb-fail">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-vb-fail-ink">{title}</p>
        {description && <p className="max-w-sm text-sm text-vb-text-2">{description}</p>}
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 rounded-vb-md border border-vb-line bg-vb-surface px-3 py-1.5 text-sm font-medium text-vb-text transition-colors hover:bg-vb-bg-subtle"
        >
          Try again
        </button>
      )}
    </div>
  );
}
